import { Mission, MissionSize, Todo } from '@/types/todo';
import { getResetDay } from '@/utils/todoHelpers';

// Maximum number of missions to keep
export const MAX_MISSIONS = 99;

// General mission constants
export const GENERAL_MISSION_NAME = "General";
export const GENERAL_MISSION_SIZE = MissionSize.S;

// Mission size requirements
export const MISSION_SIZE_REQUIREMENTS = {
  [MissionSize.S]: 3,
  [MissionSize.M]: 5,
  [MissionSize.L]: 8,
  [MissionSize.XL]: 13,
  [MissionSize.XXL]: 21,
};

// Create a new mission
export function createMission(name: string, size: MissionSize): Mission {
  return {
    id: Date.now().toString(),
    name,
    size,
    requiredTodos: MISSION_SIZE_REQUIREMENTS[size],
    completedTodos: 0,
    createdAt: new Date(),
    isCompleted: false,
  };
}

// Create a new General mission
export function createGeneralMission(): Mission {
  return {
    id: Date.now().toString(),
    name: GENERAL_MISSION_NAME,
    size: GENERAL_MISSION_SIZE,
    requiredTodos: MISSION_SIZE_REQUIREMENTS[GENERAL_MISSION_SIZE],
    completedTodos: 0,
    createdAt: new Date(),
    isCompleted: false,
    isGeneral: true,
  };
}

// Get the active General mission
export function getGeneralMission(missions: Mission[]): Mission | undefined {
  return missions.find(m => m.isGeneral && !m.isCompleted);
}

// Ensure General mission exists (create if missing)
export function ensureGeneralMissionExists(missions: Mission[]): Mission[] {
  const hasActiveGeneral = missions.some(m => m.isGeneral && !m.isCompleted);
  
  if (!hasActiveGeneral) {
    return [createGeneralMission(), ...missions];
  }
  
  return missions;
}

// Add todo to mission
export function addTodoToMission(todos: Todo[], todoId: string, missionId: string): Todo[] {
  return todos.map(todo => 
    todo.id === todoId ? { ...todo, missionId } : todo
  );
}

// Remove todo from mission
export function removeTodoFromMission(todos: Todo[], todoId: string): Todo[] {
  return todos.map(todo => 
    todo.id === todoId ? { ...todo, missionId: undefined } : todo
  );
}

// Calculate mission progress
export function calculateMissionProgress(mission: Mission, allTodos: Todo[]): Mission & { meetsRequirements: boolean } {
  const missionTodos = allTodos.filter(todo => todo.missionId === mission.id);
  const completedCount = missionTodos.filter(todo => todo.completed).length;
  const meetsRequirements = completedCount >= mission.requiredTodos;

  return {
    ...mission,
    completedTodos: completedCount,
    meetsRequirements,
  };
}

// Update mission progress for all missions
export function updateMissionProgress(missions: Mission[], allTodos: Todo[]): Mission[] {
  return missions.map(mission => calculateMissionProgress(mission, allTodos));
}

// Get todos for a specific mission
export function getMissionTodos(todos: Todo[], missionId: string): Todo[] {
  return todos.filter(todo => todo.missionId === missionId);
}

// Complete a mission
export function completeMission(missions: Mission[], missionId: string): Mission[] {
  return missions.map(mission => 
    mission.id === missionId 
      ? { ...mission, isCompleted: true }
      : mission
  );
}

// Calculate total possible points for a mission
export function calculateMissionPoints(missionId: string, allTodos: Todo[]): { totalPoints: number; todoCount: number } {
  const missionTodos = allTodos.filter(todo => todo.missionId === missionId);
  
  const totalPoints = missionTodos.reduce((sum, todo) => {
    // Calculate max possible points (early completion)
    const maxPoints = todo.isOverdrive ? 30 : 10;
    return sum + maxPoints;
  }, 0);
  
  return {
    totalPoints,
    todoCount: missionTodos.length
  };
}

// Trim missions to keep only the most recent MAX_MISSIONS
export function trimMissions(missions: Mission[]): Mission[] {
  // Always preserve the active General mission
  const activeGeneral = missions.find(m => m.isGeneral && !m.isCompleted);
  const otherMissions = missions.filter(m => !(m.isGeneral && !m.isCompleted));
  
  const sorted = otherMissions
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, MAX_MISSIONS - (activeGeneral ? 1 : 0));
  
  return activeGeneral ? [activeGeneral, ...sorted] : sorted;
}

// Storage helpers
export function saveMissions(missions: Mission[]): void {
  const trimmedMissions = trimMissions(missions);
  localStorage.setItem('missions', JSON.stringify(trimmedMissions));
}

export function loadMissions(): Mission[] {
  const stored = localStorage.getItem('missions');
  if (!stored) return [];
  
  return JSON.parse(stored).map((mission: any) => ({
    ...mission,
    createdAt: new Date(mission.createdAt),
  }));
}

// Storage keys for General mission count
const GENERAL_MISSION_COUNT_KEY = 'generalMissionCount';
const GENERAL_MISSION_COUNT_DATE_KEY = 'generalMissionCountDate';

// Load the count of General missions completed today
export function loadGeneralMissionCount(): number {
  const stored = localStorage.getItem(GENERAL_MISSION_COUNT_KEY);
  const storedDate = localStorage.getItem(GENERAL_MISSION_COUNT_DATE_KEY);
  const today = getResetDay();
  
  // If the stored date doesn't match today, reset to 0
  if (storedDate !== today) {
    return 0;
  }
  
  return stored ? parseInt(stored, 10) : 0;
}

// Save the General mission completion count
export function saveGeneralMissionCount(count: number): void {
  const today = getResetDay();
  localStorage.setItem(GENERAL_MISSION_COUNT_KEY, count.toString());
  localStorage.setItem(GENERAL_MISSION_COUNT_DATE_KEY, today);
}

// Increment the General mission completion count
export function incrementGeneralMissionCount(): number {
  const current = loadGeneralMissionCount();
  const newCount = current + 1;
  saveGeneralMissionCount(newCount);
  return current; // Return the count BEFORE incrementing
}

// Reset the General mission completion count
export function resetGeneralMissionCount(): void {
  localStorage.removeItem(GENERAL_MISSION_COUNT_KEY);
  localStorage.removeItem(GENERAL_MISSION_COUNT_DATE_KEY);
}